import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Table } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { createStructuredSelector } from 'reselect'

import { getRequestsActivitySelector } from 'Redux/selectors/analytics'
import { getRequestsActivity } from 'Redux/actions/analyticsActions'
import getCalendarMappingObject from '../helpers/calendarMappingObject'
import { countRateTotal, countRate } from '../helpers/countRates'

const CuratorsRequestsActivity = ({
  requestsActivity,
  handleGetRequestsActivity,
  pickerRanges,
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    handleGetRequestsActivity(pickerRanges)
  }, [])

  /**
   * @@@ define and count values "Total" row
   */
  const recievedRef = useRef(0)
  let { current: recievedTotal } = recievedRef
  const reviewedRef = useRef(0)
  let { current: reviewedTotal } = reviewedRef
  const reviewedRateRef = useRef(0)
  let { current: reviewedRateTotal } = reviewedRateRef
  const addedRef = useRef(0)
  let { current: addedTotal } = addedRef
  const addedRateRef = useRef(0)
  let { current: addedRateTotal } = addedRateRef

  /**
   * @@@ map a data object to the calendar
   */
  const calendar = getCalendarMappingObject(pickerRanges)

  const recievedObj = Object.entries({
    ...calendar,
    ...requestsActivity.recieved,
  })
    .reverse()
    .map(([date, recieved]) => ({ date, recieved }))

  const reviewedObj = Object.entries({
    ...calendar,
    ...requestsActivity.reviewed,
  })
    .reverse()
    .map(([date, reviewed]) => ({ date, reviewed }))

  const addedObj = Object.entries({
    ...calendar,
    ...requestsActivity.added,
  })
    .reverse()
    .map(([date, added]) => ({ date, added }))

  /**
   * @@@ complete the "table" array for render
   */
  const table = []

  Object.keys(reviewedObj).forEach(k => {
    const recievedValue = recievedObj[k].recieved
    const reviewedValue = reviewedObj[k].reviewed

    // count Total values
    recievedTotal += recievedValue
    reviewedTotal += reviewedValue
    reviewedRateTotal = countRateTotal(reviewedTotal, recievedTotal)

    // count Rate
    const reviewedRate = countRate('reviewedRate', reviewedValue, recievedValue)

    table[k] = Object.assign(recievedObj[k], reviewedObj[k], reviewedRate)
  })

  Object.keys(addedObj).forEach(k => {
    const recievedValue = recievedObj[k].recieved
    const addedValue = addedObj[k].added

    // count Total values
    addedTotal += addedValue
    addedRateTotal = countRateTotal(addedValue, recievedTotal)

    // count Rate
    const addedRate = countRate('addedRate', addedValue, recievedValue)

    table[k] = Object.assign(recievedObj[k], addedObj[k], addedRate)
  })

  return (
    <Table responsive striped style={{ overflow: 'hidden' }}>
      <thead>
        <tr>
          <th style={{ width: '18%' }} />
          <th style={{ width: '8%', textAlign: 'center' }} />
          <th style={{ width: '12%', textAlign: 'center' }}>
            {t('analytics.recieved')}
          </th>
          <th style={{ width: '11%', textAlign: 'center' }}>
            {t('campaignResultsPage.reviewed')}
          </th>
          <th style={{ width: '11%', textAlign: 'center' }}>
            {t('analytics.added')}
          </th>
          <th style={{ width: '12%', textAlign: 'center' }}>
            {t('reviewsPage.reviewRate')}
          </th>
          <th style={{ width: '14%', textAlign: 'center' }}>
            {t('analytics.addingRate')}
          </th>
          <th style={{ minWidth: '40%' }} />
          <th style={{ minWidth: '40%' }} />
        </tr>
      </thead>

      <tbody>
        <tr style={{ borderBottom: '2px solid #a6afb42b' }}>
          <td style={{ width: '18%' }}>Total</td>
          <td style={{ width: '8%' }} />
          <td style={{ width: '12%', textAlign: 'center' }}>{recievedTotal}</td>
          <td style={{ width: '11%', textAlign: 'center' }}>{reviewedTotal}</td>
          <td style={{ width: '11%', textAlign: 'center' }}>{addedTotal}</td>
          <td style={{ width: '12%', textAlign: 'center' }}>
            {reviewedRateTotal}
          </td>
          <td style={{ width: '14%', textAlign: 'center' }}>
            {addedRateTotal}
          </td>
          <td style={{ width: '40%' }} />
          <td style={{ width: '40%' }} />
        </tr>
        {table.slice(1).map(row => (
          <tr key={row.date}>
            <td style={{ width: '18%' }}>{row.date}</td>
            <td style={{ width: '8%', textAlign: 'center' }} />
            <td style={{ width: '12%', textAlign: 'center' }}>
              {row.recieved}
            </td>
            <td style={{ width: '11%', textAlign: 'center' }}>
              {row.reviewed}
            </td>
            <td style={{ width: '11%', textAlign: 'center' }}>{row.added}</td>
            <td style={{ width: '12%', textAlign: 'center' }}>
              {row.reviewedRate}
            </td>
            <td style={{ width: '14%', textAlign: 'center' }}>
              {row.addedRate}
            </td>
            <td style={{ width: '40%', textAlign: 'center' }} />
            <td style={{ width: '40%', textAlign: 'center' }} />
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

CuratorsRequestsActivity.propTypes = {
  handleGetRequestsActivity: PropTypes.func.isRequired,
  pickerRanges: PropTypes.string.isRequired,
  requestsActivity: PropTypes.shape({}).isRequired,
}

const selector = createStructuredSelector({
  requestsActivity: getRequestsActivitySelector,
})

const actions = {
  handleGetRequestsActivity: getRequestsActivity,
}

export default compose(connect(selector, actions))(CuratorsRequestsActivity)
